/**
 * MS Teams Connector - OAuth2 Delegated Flow
 * Follows Gmail connector pattern for OAuth authentication
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token from localStorage
const token = localStorage.getItem('token');

/**
 * Get MS Teams credentials and initiate OAuth flow
 * Following Gmail pattern - POST to backend to get OAuth URL
 */
export const getTeamsCredentialsAndCreateToken = async (ctdId, mcId, masterConnectors) => {
    try {
        console.log('🔑 Initiating MS Teams OAuth flow...');
        console.log('Connector ID (ctdId):', ctdId);
        console.log('Master Connector ID (mcId):', mcId);

        // 1) Find MS Teams master connector
        const teamsMasterConnector = masterConnectors.find(mc =>
            (mc.mcRecId?.toString() === mcId?.toString() ||
                mc.mcId?.toString() === mcId?.toString()) &&
            (mc.mcName === "Microsoft Teams" || mc.mcName === "Teams")
        );

        if (!teamsMasterConnector) {
            throw new Error('Microsoft Teams master connector not found');
        }

        console.log('Teams master connector found:', teamsMasterConnector);

        // 2) Store connector IDs for post-redirect completion
        localStorage.setItem('PendingTeamsConnectorId', ctdId);
        localStorage.setItem('PendingTeamsMcId', mcId);

        // 3) Build redirect URI and state
        const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
        const state = JSON.stringify({ ctdId, mcId, provider: 'teams' });

        // 4) POST to backend to get OAuth URL (with JWT auth)
        console.log('🔑 Requesting OAuth URL from backend...');

        const jwtToken = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}synctrack/teams/auth-url`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwtToken}`
            },
            body: JSON.stringify({
                mcId: mcId,
                redirectUri: redirectUri,
                state: state
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Backend error:', errorText);
            throw new Error(`Failed to get auth URL: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Got OAuth URL, redirecting to Microsoft...');

        // Redirect to Microsoft OAuth
        window.location.href = data.authUrl;

        // Return null to indicate we're redirecting (prevents status update)
        return null;

    } catch (error) {
        console.error('Error initiating Teams OAuth:', error);
        throw error;
    }
};

/**
 * Exchange Teams OAuth authorization code for access token
 * Called after user grants permission and returns from Microsoft
 */
export const exchangeTeamsAuthCodeForToken = async (authCode, ctdId, mcId) => {
    try {
        console.log('🔄 Exchanging Teams auth code for token...');
        console.log('Auth Code:', authCode.substring(0, 20) + '...');
        console.log('Connector ID:', ctdId);
        console.log('Master Connector ID:', mcId);

        // Get JWT token from localStorage
        const jwtToken = localStorage.getItem('token');

        // Call backend to exchange auth code for tokens
        const response = await fetch(`${BACKEND_URL}synctrack/teams/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json',
                'Authorization': `Bearer ${jwtToken}`
            },
            body: new URLSearchParams({
                code: authCode,
                mcId: mcId
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Backend error response:', errorText);
            throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
        }

        const tokenData = await response.json();

        console.log('✅ Teams token received successfully');
        console.log('Access Token:', tokenData.access_token ? `${tokenData.access_token.substring(0, 30)}...` : 'Not provided');
        console.log('Refresh Token:', tokenData.refresh_token ? `${tokenData.refresh_token.substring(0, 20)}...` : 'Not provided');
        console.log('Expires In:', tokenData.expires_in, 'seconds');

        return {
            access_token: tokenData.access_token,
            refresh_token: tokenData.refresh_token,
            expires_in: tokenData.expires_in || 3600,
            scope: tokenData.scope,
            token_type: tokenData.token_type || 'Bearer'
        };

    } catch (error) {
        console.error('Error exchanging Teams auth code:', error);
        throw error;
    }
};

/**
 * Refresh Teams access token using refresh token
 */
export const refreshTeamsToken = async (refreshToken, mcId) => {
    try {
        console.log('🔄 Refreshing Teams token...');

        const jwtToken = localStorage.getItem('token');

        const response = await fetch(`${BACKEND_URL}synctrack/teams/refreshToken`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json',
                'Authorization': `Bearer ${jwtToken}`
            },
            body: new URLSearchParams({
                refreshToken: refreshToken,
                mcId: mcId
            })
        });

        if (!response.ok) {
            throw new Error(`Token refresh failed: ${response.status}`);
        }

        const tokenData = await response.json();

        console.log('✅ Teams token refreshed successfully');

        return {
            access_token: tokenData.access_token,
            refresh_token: tokenData.refresh_token || refreshToken, // Keep old refresh token if not returned
            expires_in: tokenData.expires_in || 3600
        };

    } catch (error) {
        console.error('Error refreshing Teams token:', error);
        throw error;
    }
};
